import { useEffect, useRef, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { api, ApiError } from '../../api/client'
import type { Media, Trip } from '../../api/types'
import { ensureSettings, keys, useUploadMedia } from '../../hooks/queries'
import { prepareImage } from '../../lib/images'
import { TrashIcon } from '../../components/Icons'
import { Ausschnittwahl } from '../../components/Ausschnittwahl'
import { ausschnittAus, type Ausschnitt } from '../../lib/ausschnitt'
import { useTexte, type Texte } from '../../texte'

/**
 * Der Hintergrund des Chats — eine Farbe oder ein Bild.
 *
 * **Nur der Reisende**, und er gilt für alle Mitlesenden. Der Bericht ist
 * seiner; ein Hintergrund, den jeder für sich dreht, wäre eine andere
 * Entscheidung gewesen und ist es nicht geworden.
 *
 * **Der Deckgrad ist der eigentliche Regler.** Ein Reisefoto bei voller
 * Stärke verschluckt jede Schrift — deshalb ist die Vorgabe blass, und der
 * Regler zeigt sofort, was er tut.
 */
/**
 * Die Farbe steht hier, ihr Name in `texte/de.ts` (`reise.grund.farben`).
 *
 * Derselbe Schnitt wie bei den Nadeln und den Marken der Umgebungssuche: die
 * Mechanik bleibt, wo sie wirkt. Der Schlüssel ist aus dem Textzweig
 * abgeleitet — eine Farbe ohne Namen bemerkt so der Übersetzer.
 */
type Farbname = keyof Texte['reise']['grund']['farben']

const FARBEN: { wert: string; name: Farbname }[] = [
  { wert: '', name: 'ohne' },
  { wert: '#efeae2', name: 'sand' },
  { wert: '#e3ebe6', name: 'salbei' },
  { wert: '#e6e9f0', name: 'nebel' },
  { wert: '#f2e8e8', name: 'rose' },
  { wert: '#1f2c33', name: 'nacht' },
]

export function Chatgrund({ trip }: { trip: Trip }) {
  const texte = useTexte()
  const t = texte.reise.grund
  const upload = useUploadMedia(trip.id)
  const client = useQueryClient()
  const feld = useRef<HTMLInputElement>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const grund = trip.chat_bg ?? {
    color: null,
    url: null,
    opacity: 35,
    x: 50,
    y: 50,
    zoom: 100,
    width: 0,
    height: 0,
  }
  const [waehlt, setWaehlt] = useState(false)
  const ausschnitt = ausschnittAus(grund)

  /**
   * **Der Regler hält seinen Wert selbst, während der Finger darauf liegt.**
   *
   * Aus dem Betrieb: „die Hintergrundbild-Deckung wird nicht gespeichert.
   * außerdem: der Schieberegler speichert bei jeder kleinen Änderung."
   *
   * Beides hatte dieselbe Ursache. Der Regler stand vollständig auf dem Wert
   * vom Server, und jede Bewegung schickte sofort eine Anfrage. Solange die
   * lief, war er `disabled` — er blendete sich also **mitten im Ziehen selbst
   * ab** und sprang auf den alten Wert zurück, weil der neue erst mit der
   * Antwort kam. Auf dem Telefon bricht damit die Geste ab, und gespeichert
   * wurde am Ende nichts oder etwas Zufälliges.
   *
   * Jetzt: der Wert liegt hier, die Bewegung ist sofort zu sehen, und
   * geschickt wird **einmal, beim Loslassen**. Kommt der Wert von außen (eine
   * andere Sitzung, ein Neuladen), zieht der Effekt nach.
   */
  const [deckgrad, setDeckgrad] = useState(grund.opacity)
  useEffect(() => {
    setDeckgrad(grund.opacity)
  }, [grund.opacity])

  /**
   * **Loslassen heißt `change`, Ziehen heißt `input`** — und React kennt nur
   * das eine: sein `onChange` ist das DOM-Ereignis `input` und feuert bei
   * jedem Zwischenwert. Das echte `change` gibt es am Regler trotzdem, es
   * kommt beim Loslassen (und bei jedem Tastendruck, wer ihn mit den Pfeilen
   * bedient — auch das ist eine abgeschlossene Handlung).
   *
   * Deshalb hier von Hand am Element und nicht über eine Eigenschaft.
   */
  const reglerRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    const el = reglerRef.current
    if (el === null) return
    const fertig = (): void => {
      void sichere({ opacity: Number(el.value) })
    }
    el.addEventListener('change', fertig)
    return () => el.removeEventListener('change', fertig)
  })

  async function sichere(teil: {
    color?: string | null
    media_id?: number | null
    opacity?: number
    ausschnitt?: Ausschnitt
  }) {
    setProblem(null)
    setLaeuft(true)
    try {
      await api.post<Trip>(`/api/trips/${trip.id}/background`, {
        color: teil.color !== undefined ? (teil.color ?? '') : (grund.color ?? ''),
        // Die id kennt die Oberfläche nur aus der Adresse; beim Ändern von
        // Farbe oder Deckgrad bleibt sie, was sie war.
        media_id: teil.media_id !== undefined ? teil.media_id : bildId(grund.url),
        opacity: teil.opacity !== undefined ? teil.opacity : grund.opacity,
        // Auch der Ausschnitt geht bei jedem Zug mit -- dieser Weg schickt
        // seit jeher den ganzen Zustand, und was fehlt, setzte der Server
        // sonst auf „mittig, füllend" zurück.
        ...(teil.ausschnitt ?? ausschnitt),
      })
      // Nur die Reise -- der Hintergrund steht an ihr und sonst nirgends.
      // `invalidateQueries()` ohne Schlüssel holte bei jedem Reglerschritt
      // die ganze Anwendung neu.
      await client.invalidateQueries({ queryKey: keys.trip(trip.id) })
      await client.invalidateQueries({ queryKey: keys.trips })
    } catch (fehler) {
      setProblem(
        fehler instanceof ApiError && fehler.offline
          ? texte.allgemein.ohneNetz
          : fehler instanceof Error
            ? fehler.message
            : texte.allgemein.schiefgelaufen,
      )
    } finally {
      setLaeuft(false)
    }
  }

  async function waehleBild(datei: File | undefined) {
    if (!datei) return
    setProblem(null)
    setLaeuft(true)
    try {
      const werte = await ensureSettings(client)
      const fertig = await prepareImage(datei, {
        ...(werte ? { maxEdge: werte.image_max_edge, quality: werte.image_quality } : {}),
        reiseVersatz: trip.tz_offset_min ?? null,
      })
      const medium: Media = await upload.mutateAsync({ blob: fertig.blob })
      await sichere({ media_id: medium.id })
    } catch (fehler) {
      setProblem(fehler instanceof Error ? fehler.message : texte.reise.symbolbild.bildFehlgeschlagen)
      setLaeuft(false)
    }
  }

  return (
    <div className="card card--seite">
      <h2 className="card__titel">{t.titel}</h2>
      <p className="card__lead">{t.lead}</p>

      <div className="grundfarben">
        {FARBEN.map((farbe) => (
          <button
            key={farbe.wert}
            type="button"
            className={
              (grund.color ?? '') === farbe.wert
                ? 'grundfarbe grundfarbe--an'
                : 'grundfarbe'
            }
            style={farbe.wert === '' ? undefined : { background: farbe.wert }}
            title={t.farben[farbe.name]}
            aria-label={t.farben[farbe.name]}
            aria-pressed={(grund.color ?? '') === farbe.wert}
            disabled={laeuft}
            onClick={() => void sichere({ color: farbe.wert })}
          >
            {farbe.wert === '' ? '—' : ''}
          </button>
        ))}

        {/* **Und eine freie Farbe.** Die sechs Vorschläge bleiben der
            Normalfall — sie sind auf Lesbarkeit hin gewählt, und wer schnell
            etwas Passendes will, tippt einmal. Wer eine bestimmte Farbe im
            Kopf hat, bekommt sie hier; das Feld öffnet den Farbwähler des
            Geräts.

            **Gespeichert wird erst beim Loslassen** (`onChange`), nicht
            während des Ziehens: ein Farbwähler feuert bei jedem Pixel, und
            das wären dreißig Anfragen für eine Entscheidung. Dieselbe
            Überlegung wie beim Regler für die Nadelbreite. */}
        <input
          type="color"
          className="grundfarbe grundfarbe--frei"
          value={grund.color ?? '#efeae2'}
          title={t.eigeneFarbe}
          aria-label={t.eigeneFarbe}
          disabled={laeuft}
          onChange={(event) => void sichere({ color: event.target.value })}
        />
      </div>

      <p className="feld__hinweis" style={{ marginTop: 6 }}>
        {t.farbHinweis}
        {grund.color !== null && grund.color !== '' && t.zurzeit(grund.color)}
      </p>

      <div className="symbolbild" style={{ marginTop: 12 }}>
        {grund.url ? (
          <img className="symbolbild__vorschau" src={grund.url} alt="" />
        ) : (
          <span className="symbolbild__leer" aria-hidden="true">
            —
          </span>
        )}

        <div className="symbolbild__knoepfe">
          <input
            ref={feld}
            type="file"
            accept="image/*"
            hidden
            onChange={(event) => {
              void waehleBild(event.target.files?.[0])
              event.target.value = ''
            }}
          />
          <button
            type="button"
            className="button button--small"
            disabled={laeuft}
            onClick={() => feld.current?.click()}
          >
            {laeuft
              ? texte.allgemein.laeuft
              : grund.url
                ? texte.reise.symbolbild.anderesBild
                : texte.reise.symbolbild.bildWaehlen}
          </button>
          {grund.url && (
            <button
              type="button"
              className="button button--small button--outline"
              disabled={laeuft}
              onClick={() => setWaehlt(true)}
            >
              {texte.reise.symbolbild.ausschnitt}
            </button>
          )}
          {grund.url && (
            <button
              type="button"
              className="button button--small button--outline"
              disabled={laeuft}
              onClick={() => void sichere({ media_id: null })}
            >
              <TrashIcon size={14} /> Bild weg
            </button>
          )}
        </div>
      </div>

      {/* Der Regler steht nur da, wo es ein Bild gibt — ein Deckgrad ohne
          Bild regelt nichts. */}
      {grund.url && (
        <label className="feld" style={{ marginTop: 12 }}>
          <span className="feld__name">{t.deckgrad(deckgrad)}</span>
          <input
            ref={reglerRef}
            type="range"
            min={5}
            max={100}
            step={5}
            value={deckgrad}
            /* **Kein `disabled` mehr.** Genau das hat die Geste abgebrochen:
               während die Anfrage lief, war der Regler abgeblendet, und der
               Finger zog ins Leere. Eine laufende Anfrage ist kein Grund,
               den Regler anzuhalten -- die letzte gewinnt ohnehin. */
            onChange={(event) => setDeckgrad(Number(event.target.value))}
          />
          <span className="feld__hinweis">
            Je kräftiger das Bild, desto schwerer ist die Schrift zu lesen.
          </span>
        </label>
      )}

      {problem && (
        <p className="feld__hinweis" role="alert">
          {problem}
        </p>
      )}

      {/* **Die Form des Inhaltsbereichs, nicht ein fester Rahmen.** So sieht
          der Chat auf *diesem* Gerät aus — am iPhone hochkant, am iPad fast
          quadratisch. Und die Größe darf hier unter „füllt die Fläche": dann
          steht das ganze Bild auf der gewählten Farbe. */}
      {waehlt && grund.url && (
        <Ausschnittwahl
          titel={t.ausschnittTitel}
          bildUrl={grund.url}
          ausschnitt={ausschnitt}
          form={zielform()}
          kleinste={30}
          onFertig={(neu) => {
            setWaehlt(false)
            void sichere({ ausschnitt: neu })
          }}
          onSchliessen={() => setWaehlt(false)}
        />
      )}
    </div>
  )
}

/**
 * Die Form der Fläche, die der Chat auf diesem Gerät einnimmt.
 *
 * Gemessen und nicht geraten: am iPhone ist der Verlauf das ganze Fenster, am
 * iPad nur die rechte Spalte. Ein Rahmen in der falschen Form zeigte einen
 * Ausschnitt, den es hier gar nicht gibt.
 */
function zielform(): { breite: number; hoehe: number } {
  const el = document.querySelector('.main')
  const kasten = el?.getBoundingClientRect()
  if (kasten === undefined || kasten.width < 1 || kasten.height < 1) {
    return { breite: window.innerWidth, hoehe: window.innerHeight }
  }
  return { breite: kasten.width, hoehe: kasten.height }
}

/**
 * Die id aus der Adresse `/api/media/42/file`.
 *
 * Die Reise liefert den Hintergrund als Adresse aus, damit die Oberfläche
 * keine zusammenbauen muss — beim Zurückschicken braucht der Server aber die
 * Zahl. Sie hier herauszulesen ist der kleinere Schaden, als sie zusätzlich
 * mitzuliefern und an zwei Stellen gleich halten zu müssen.
 */
function bildId(url: string | null): number | null {
  const treffer = url?.match(/\/api\/media\/(\d+)\/file/)
  return treffer ? Number(treffer[1]) : null
}
